import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Spinner,
} from '@fluentui/react-components';
import { Search16Regular } from '@fluentui/react-icons';
import type { QcTemplateRule, Severity } from '@qcbot/shared';
import { fetchQcTemplateRules } from '../api';

const SEVERITIES: Severity[] = ['Critical', 'High', 'Medium', 'Low', 'Info'];

/** Read-only viewer for the QC rules the selected template is being graded against — opened from
 * a link in the template-selection/results views so a reviewer can check what's actually being
 * checked, even before starting a run. Supports a keyword search (title/description) and a
 * severity filter, both applied client-side over the already-fetched rule list. */
export function TemplateRulesDialog({ open, onClose, templateId }: { open: boolean; onClose: () => void; templateId: string }) {
  const [rules, setRules] = useState<QcTemplateRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState<Set<Severity>>(new Set());

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    fetchQcTemplateRules(templateId)
      .then((rows) => setRules([...rows].sort((a, b) => a.sortOrder - b.sortOrder)))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, templateId]);

  function toggleSeverity(severity: Severity) {
    setSeverityFilter((prev) => {
      const next = new Set(prev);
      if (next.has(severity)) next.delete(severity);
      else next.add(severity);
      return next;
    });
  }

  const visibleRules = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return rules
      .filter((r) => r.enabled)
      .filter((r) => severityFilter.size === 0 || (r.severityGuidance && severityFilter.has(r.severityGuidance)))
      .filter((r) => !keyword || r.title.toLowerCase().includes(keyword) || (r.description ?? '').toLowerCase().includes(keyword));
  }, [rules, search, severityFilter]);

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface style={{ margin: 16 }}>
        <DialogBody>
          <DialogTitle>Template QC rules</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
              <Input
                value={search}
                onChange={(_, d) => setSearch(d.value)}
                placeholder="Search by keyword…"
                contentBefore={<Search16Regular style={{ color: '#666' }} />}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {SEVERITIES.map((severity) => (
                  <Checkbox
                    key={severity}
                    label={severity}
                    checked={severityFilter.has(severity)}
                    onChange={() => toggleSeverity(severity)}
                  />
                ))}
              </div>
            </div>
            <div style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {loading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Spinner size="tiny" /> Loading rules…
                </div>
              )}
              {error && <div style={{ color: '#b91c1c', fontSize: 13 }}>{error}</div>}
              {!loading && !error && rules.filter((r) => r.enabled).length === 0 && (
                <div style={{ fontSize: 13, color: '#666' }}>This template has no rules yet.</div>
              )}
              {!loading && !error && rules.filter((r) => r.enabled).length > 0 && visibleRules.length === 0 && (
                <div style={{ fontSize: 13, color: '#666' }}>No rules match this search/filter.</div>
              )}
              {visibleRules.map((rule) => (
                <div key={rule.id} style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: '8px 10px' }}>
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{rule.severityGuidance ?? 'No default severity'}</div>
                  <div style={{ fontSize: 13 }}>{rule.title}</div>
                  {rule.description && <div style={{ fontSize: 12, color: '#444', marginTop: 2 }}>{rule.description}</div>}
                </div>
              ))}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
